import { ReactElement } from 'react';
import PropTypes from 'prop-types';
import { useField } from 'formik';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import RequiredIndicator from 'pages/ApplicationRequest/components/containers/RequiredIndicator';
import CustomFormControl from './CustomFormControl';
import CustomHelperText from './CustomHelperText';
import { BEM_NAME_ELEMENTS } from 'pages/NewNonEmployee/_const';

/**
 * customized field prompting user for option selection
 * @param {string|ReactElement} label label to display
 * @param {boolean} required if required, display an indicator
 * @param {object[]} options list of options to select from containing `label` to display and `value` to store in state
 * @param {string|ReactElement} helperText default on-screen assistance
 * @param {any} props properties to be passed to the select
 * @returns {ReactElement} select prompting for user input
 */
const CustomSelect = ({
	label,
	required,
	options = [],
	helperText,
	...props
}) => {
	const [field, meta] = useField(props);
	return (
		<CustomFormControl error={meta.touched && !!meta.error}>
			<InputLabel
				id={props.name + '-select-label'}
				sx={{ backgroundColor: '#fff', pl: 1, pr: 1 }}
			>
				<>
					{required && <RequiredIndicator />}
					{label}
				</>
			</InputLabel>
			<Select
				labelId={props.name + '-select-label'}
				variant='outlined'
				{...props}
				{...field}
				inputProps={{
					id: `${BEM_NAME_ELEMENTS.VIEW_FORM}__${BEM_NAME_ELEMENTS.FIELD_SELECT}__${props.name}`,
				}}
			>
				<MenuItem
					key='no-value'
					value=''
					id={`${BEM_NAME_ELEMENTS.VIEW_FORM}__${BEM_NAME_ELEMENTS.FIELD_SELECT}__${props.name}--option-none}`}
				>
					<em>None</em>
				</MenuItem>
				{options.map(option => (
					<MenuItem
						key={option.value}
						value={option.value}
						id={`${BEM_NAME_ELEMENTS.VIEW_FORM}__${BEM_NAME_ELEMENTS.FIELD_SELECT}__${props.name}--option-${option.label}`}
					>
						{option.label}
					</MenuItem>
				))}
			</Select>
			<CustomHelperText
				error={meta.error}
				touched={meta.touched}
				helperText={helperText}
			/>
		</CustomFormControl>
	);
};

CustomSelect.propTypes = {
	label: PropTypes.string.isRequired,
	required: PropTypes.bool,
	options: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.string.isRequired,
			value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
				.isRequired,
		})
	),
	helperText: PropTypes.string,
	name: PropTypes.string.isRequired,
};

export default CustomSelect;
